
import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Text,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Button,
  Container,
  Spinner,
  Icon,
  useColorModeValue,
  useToast
} from '@chakra-ui/react';
import { MdCategory, MdCircle, MdRefresh } from 'react-icons/md';
import { CategoryService, PlaidCategory } from '../services/category.service';
import { useAuth } from '../hooks/useAuth';
import { toUserMessage } from '../lib/apiError';

interface CategoryNode {
  id: string;
  label: string;
  hierarchy: string[];
  children: CategoryNode[];
}

export const CategoriesPage: React.FC = () => {
  console.log('CategoriesPage: Mounting...');
  const { session } = useAuth();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; });
  const hasSession = !!session?.access_token;
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [rootCategories, setRootCategories] = useState<CategoryNode[]>([]);

  // Colors
  const textColor = useColorModeValue('gray.800', 'white');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');
  const leafColor = useColorModeValue('black', 'gray.400');
  const subHeaderColor = useColorModeValue('black', 'white');

  useEffect(() => {
    console.log('CategoriesPage: useEffect fired');
    if (session?.access_token) {
      fetchCategories();
    }
  }, [hasSession]);

  const fetchCategories = async () => {
    if (!sessionRef.current?.access_token) {
      console.error('CategoriesPage: No access token');
      setLoading(false);
      return;
    }
    try {
      console.log('CategoriesPage: Fetching categories...');
      const cats = await CategoryService.getAllCategories(sessionRef.current!.access_token);
      console.log('CategoriesPage: Fetched', cats.length, 'categories');
      const tree = buildTree(cats);
      console.log('CategoriesPage: Tree built', tree.length, 'root nodes');
      setRootCategories(tree);
    } catch (error) {
      console.error('CategoriesPage: Failed to load categories', error);
      toast({
        title: 'Error loading categories',
        description: toUserMessage(error, 'Please try again in a moment.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshCategories = async () => {
    if (!sessionRef.current?.access_token) {
      toast({
        title: 'Not authenticated',
        description: 'Please log in to refresh categories',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setRefreshing(true);
    try {
      console.log('CategoriesPage: Refreshing categories from Plaid...');
      const result = await CategoryService.refreshCategories(sessionRef.current!.access_token);

      if (result.success) {
        toast({
          title: 'Categories refreshed',
          description: `Loaded ${result.categoryCount} categories from Plaid`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        // Reload the categories
        await fetchCategories();
      } else {
        throw new Error(result.error || 'Failed to refresh');
      }
    } catch (error) {
      console.error('CategoriesPage: Failed to refresh categories', error);
      toast({
        title: 'Error refreshing categories',
        description: toUserMessage(error, 'Please try again in a moment.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRefreshing(false);
    }
  };

  const buildTree = (categories: PlaidCategory[]): CategoryNode[] => {
    const tree: CategoryNode[] = [];
    
    categories.forEach(cat => {
      let currentLevel = tree;
      const pathParts: string[] = [];

      cat.hierarchy.forEach((part: string, index: number) => {
        pathParts.push(part);
        const id = pathParts.join('-');
        
        let existingNode = currentLevel.find(n => n.label === part);
        
        if (!existingNode) {
            existingNode = {
                id,
                label: part,
                hierarchy: [...pathParts],
                children: [] 
            };
            currentLevel.push(existingNode);
        }
        currentLevel = existingNode.children;
      });
    });

    // Recursive sort
    const sortTree = (nodes: CategoryNode[]) => {
        nodes.sort((a, b) => a.label.localeCompare(b.label));
        nodes.forEach(n => sortTree(n.children));
    };
    
    sortTree(tree);
    return tree;
  };

  const renderNode = (node: CategoryNode, depth: number = 0) => {
    const isLeaf = node.children.length === 0;

    if (isLeaf) {
      return (
        <Box 
            key={node.id} 
            pl={4} 
            py={2} 
            borderLeft="1px" 
            borderColor="gray.700"
            _hover={{ bg: 'whiteAlpha.100' }}
        >
          <Text fontSize="sm" color={leafColor}>{node.label}</Text>
        </Box>
      );
    }

    // For top-level nodes, use a distinct style
    if (depth === 0) {
        return (
            <AccordionItem key={node.id} border="none" mb={4}>
                <h2>
                    <AccordionButton 
                        bg="bg.muted" 
                        color="accent.primary"
                        _hover={{ bg: 'xacto.activeNavy' }}
                        borderRadius="md"
                        py={3}
                    >
                        <Box flex="1" textAlign="left" fontWeight="bold" display="flex" alignItems="center">
                            <Icon as={MdCategory} mr={2} />
                            {node.label}
                            <Badge ml={3} colorScheme="cyan" variant="subtle" fontSize="0.7em">
                                {node.children.length} Subcategories
                            </Badge>
                        </Box>
                        <AccordionIcon />
                    </AccordionButton>
                </h2>
                <AccordionPanel pb={4} bg="blackAlpha.200">
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                       {/* Render level 2 items as cards or lists */}
                       {node.children.map(child => (
                           <Box key={child.id}>
                               <Text fontWeight="semibold" color={subHeaderColor} mb={2} display="flex" alignItems="center">
                                    <Icon as={MdCircle} boxSize={2} mr={2} color="cyan.500" />
                                    {child.label}
                               </Text>
                               <VStack align="stretch" spacing={0} pl={4}>
                                   {child.children.length > 0 ? (
                                       child.children.map(sub => renderNode(sub, depth + 2))
                                   ) : (
                                       <Text fontSize="xs" color="gray.500" fontStyle="italic">No deeper levels</Text>
                                   )}
                               </VStack>
                           </Box>
                       ))}
                    </SimpleGrid>
                </AccordionPanel>
            </AccordionItem>
        );
    }

    // Mid-level nodes (recursive fallback, though we handled depth 1 explicit above)
    return (
        <Box key={node.id} pl={4}>
            <Text fontWeight="bold">{node.label}</Text>
             {node.children.map(child => renderNode(child, depth + 1))}
        </Box>
    );
  };

  if (loading) {
    return (
        <Container centerContent py={20}>
            <Spinner color="cyan.400" size="xl" />
        </Container>
    );
  }

  return (
    <Box p={8} bg="bg.surface" minH="100vh">
      <HStack justify="space-between" align="center" mb={8}>
        <Heading color={textColor}>Categories</Heading>
        <Button
          leftIcon={<Icon as={MdRefresh} />}
          colorScheme="cyan"
          variant="outline"
          size="sm"
          onClick={handleRefreshCategories}
          isLoading={refreshing}
          loadingText="Fetching..."
        >
          Fetch Categories
        </Button>
      </HStack>
      <Text color={subTextColor} mb={8}>
        Explore the standard Plaid taxonomy used to categorize your transactions.
        {rootCategories.length === 0 && !loading && (
          <Text as="span" color="orange.400" ml={2}>
            No categories loaded. Click "Fetch Categories" to load from Plaid.
          </Text>
        )}
      </Text>

      <Accordion allowMultiple defaultIndex={[]}>
        {rootCategories.map(node => renderNode(node, 0))}
      </Accordion>
    </Box>
  );
};
